import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore.js';
import { useThemeStore } from '../../stores/themeStore.js';
import { useStorageUrl } from '../../hooks/useStorageUrl.js';
import { useAccountPath } from '../../hooks/useAccountPath.js';
import { accountSubtitle } from '../../utils/accountSubtitle.js';
import { webProfileUrl } from '../../utils/webAppLinks.js';
import { Pfp } from './Pfp.jsx';
import { IconLeaveChat, IconMoon, IconPfp, IconSettings, IconSun } from './SidebarIcons.jsx';
import { SidebarConfirm } from './SidebarConfirm.jsx';
import { SidebarRow } from './SidebarRow.jsx';
import { SidebarStyle } from './sidebarStyle.js';

const { Account, Menu } = SidebarStyle;

export function AccountChip({ geometry }) {
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const avatarUrl = useStorageUrl(user?.avatar_path);
  const navigate = useNavigate();
  const account = useAccountPath();
  const anchorRef = useRef(null);
  const rowRef = useRef(null);
  const gearRef = useRef(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [portalTarget, setPortalTarget] = useState(null);
  const [confirmLogout, setConfirmLogout] = useState(false);

  useEffect(() => {
    if (geometry.rail) setMenuAnchor(null);
  }, [geometry.rail]);

  const focusMenuButton = useCallback(() => {
    anchorRef.current?.querySelector('button')?.focus();
  }, []);

  const closeMenu = useCallback(() => {
    setMenuAnchor(null);
    focusMenuButton();
  }, [focusMenuButton]);

  const closeLogoutConfirm = useCallback(() => {
    setConfirmLogout(false);
    focusMenuButton();
  }, [focusMenuButton]);

  if (!user) return null;

  const name = user.display_name || user.email;

  const toggleMenu = () => {
    setPortalTarget(anchorRef.current?.closest('dialog') ?? document.body);
    const rect = rowRef.current?.getBoundingClientRect();
    setMenuAnchor((open) => (open != null || rect == null ? null : { left: rect.left, top: rect.top }));
  };

  return (
    <div
      ref={anchorRef}
      className="vs-account"
      style={{ padding: geometry.rail ? SidebarStyle.EDGE_PADDING : Account.INSET }}
    >
      {menuAnchor != null && createPortal(
        <AccountMenu
          anchorBounds={menuAnchor}
          triggerRef={gearRef}
          onSettingsClick={() => { closeMenu(); navigate(account()); }}
          onLogoutClick={() => { closeMenu(); setConfirmLogout(true); }}
          onDismiss={closeMenu}
        />,
        portalTarget ?? document.body,
      )}

      {confirmLogout && createPortal(
        <SidebarConfirm
          text="Are you sure you want to logout?"
          onClose={closeLogoutConfirm}
          onConfirm={() => {
            logout().catch((error) => console.error('Could not complete the sign-out', error));
          }}
        />,
        document.body,
      )}

      <div ref={rowRef} className="vs-account-profile">
        <SidebarRow
          geometry={geometry}
          label={name}
          href={webProfileUrl()}
          external
          inset={Account.PFP_INSET}
          height={Account.PFP_SIZE + 2 * Account.PFP_INSET}
          iconSlot={Account.PFP_SIZE}
          iconContent={<Pfp url={avatarUrl} size={Account.PFP_SIZE} />}
          textContent={
            <span className="vs-lines">
              <span className="vs-line" style={{ color: 'var(--vs-text)', fontSize: 14, fontWeight: 500 }}>
                {name}
              </span>
              <span className="vs-line" style={{ color: 'var(--vs-hint)', fontSize: 12, fontWeight: 500 }}>
                {accountSubtitle(user)}
              </span>
            </span>
          }
        />
      </div>

      <div
        ref={gearRef}
        className={`vs-account-gear${geometry.rail ? ' is-hidden' : ''}`}
        style={geometry.rail ? { width: 0, marginLeft: 0 } : { width: SidebarStyle.ICON_SLOT, marginLeft: 4 }}
      >
        <SidebarRow
          geometry={geometry}
          label="Account menu"
          menuExpanded={menuAnchor != null}
          onClick={toggleMenu}
          iconContent={<IconSettings size={18} style={{ color: 'var(--vs-hint)' }} />}
        />
      </div>
    </div>
  );
}

const ITEM_SELECTOR = '[role="menuitem"], [role="menuitemradio"]';

function AccountMenu({ anchorBounds, triggerRef, onSettingsClick, onLogoutClick, onDismiss }) {
  const menuRef = useRef(null);

  useEffect(() => {
    const menu = menuRef.current;
    const focused = document.activeElement;
    // Opened from the keyboard, the menu starts on its first item; opened by a pointer, it holds
    // the focus itself so no item shows a keyboard ring until the keyboard is used
    if (menu != null && !menu.contains(focused)) {
      const byKeyboard = triggerRef.current?.contains(focused) && focused.matches(':focus-visible');
      (byKeyboard ? menu.querySelector('[role="menuitem"]') : menu)?.focus();
    }

    const onPointerDown = (event) => {
      if (menu?.contains(event.target) || triggerRef.current?.contains(event.target)) return;
      onDismiss();
    };
    document.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('resize', onDismiss);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('resize', onDismiss);
    };
  }, [onDismiss, triggerRef]);

  const onKeyDown = (event) => {
    const items = Array.from(menuRef.current?.querySelectorAll(ITEM_SELECTOR) ?? []);
    const index = items.indexOf(document.activeElement);
    if (event.key === 'Escape') { event.preventDefault(); onDismiss(); }
    else if (event.key === 'Tab') onDismiss();
    else if (event.key === 'Home') { event.preventDefault(); items[0]?.focus(); }
    else if (event.key === 'End') { event.preventDefault(); items[items.length - 1]?.focus(); }
    else if (event.key === 'ArrowDown') { event.preventDefault(); items[(index + 1) % items.length]?.focus(); }
    else if (event.key === 'ArrowUp') { event.preventDefault(); items[(Math.max(index, 0) - 1 + items.length) % items.length]?.focus(); }
  };

  return (
    <div
      ref={menuRef}
      role="menu"
      aria-label="Account menu"
      tabIndex={-1}
      className="vs vs-menu"
      onKeyDown={onKeyDown}
      style={{
        left: anchorBounds.left,
        bottom: window.innerHeight - anchorBounds.top + Menu.ANCHOR_GAP,
      }}
    >
      <button type="button" role="menuitem" className="vs-menu-item" onClick={onSettingsClick}>
        <IconPfp size={16} style={{ color: 'var(--vs-hint)' }} />
        Account Settings
      </button>

      <ThemePicker />

      <button type="button" role="menuitem" className="vs-menu-item" onClick={onLogoutClick}>
        <IconLeaveChat size={16} style={{ color: 'var(--vs-hint)' }} />
        Logout
      </button>
    </div>
  );
}

const THEME_OPTIONS = [
  { value: 'light', label: 'Light' },
  { value: 'system', label: 'Auto' },
  { value: 'dark', label: 'Dark' },
];

function ThemePicker() {
  const preference = useThemeStore((state) => state.preference);
  const setPreference = useThemeStore((state) => state.setPreference);
  const activeIndex = Math.max(THEME_OPTIONS.findIndex((option) => option.value === preference), 0);

  const onKeyDown = (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    const tabs = Array.from(event.currentTarget.querySelectorAll('[data-active]'));
    const index = tabs.indexOf(document.activeElement);
    if (index < 0) return;
    event.preventDefault();
    const next = tabs[(index + (event.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length];
    next?.click();
    next?.focus();
  };

  return (
    <div className="vs-theme">
      <IconSun size={16} />
      <div
        role="group"
        aria-label="Theme"
        className="vs-segments"
        style={{ '--vs-tabs': THEME_OPTIONS.length, '--vs-tab': activeIndex }}
        onKeyDown={onKeyDown}
      >
        {THEME_OPTIONS.map((option) => {
          const active = option.value === preference;
          return (
            <button
              key={option.value}
              type="button"
              role="menuitemradio"
              aria-checked={active}
              tabIndex={active ? 0 : -1}
              data-active={active}
              className={`vs-segment${active ? ' is-active' : ''}`}
              onClick={() => setPreference(option.value)}
            >
              {option.label}
            </button>
          );
        })}
      </div>
      <IconMoon size={16} />
    </div>
  );
}
